// Thin wrapper around the Telegram WebApp global. We load the SDK script in
// index.html so the global is always present in the real client; outside
// Telegram (local browser dev) the wrapper falls back to safe stubs so the UI
// still mounts.

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

type ThemeParams = Record<string, string | undefined>;

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      is_premium?: boolean;
    };
  };
  themeParams: ThemeParams;
  colorScheme: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  close: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  CloudStorage?: {
    setItem: (key: string, value: string, cb?: (err: string | null, ok?: boolean) => void) => void;
    getItem: (key: string, cb: (err: string | null, value?: string) => void) => void;
    removeItem: (key: string, cb?: (err: string | null, ok?: boolean) => void) => void;
  };
  openInvoice?: (url: string, cb: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void) => void;
  showPopup?: (params: { title?: string; message: string; buttons?: { id?: string; type?: string; text?: string }[] }, cb?: (id: string) => void) => void;
  openLink?: (url: string, opts?: { try_instant_view?: boolean }) => void;
  openTelegramLink?: (url: string) => void;
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function initTelegram(): TelegramWebApp | null {
  const wa = getWebApp();
  if (!wa) return null;
  wa.ready();
  wa.expand();
  // Match the paper aesthetic so the chrome blends with the app.
  try {
    wa.setHeaderColor('#f4f0e8');
    wa.setBackgroundColor('#f4f0e8');
  } catch {
    // older clients reject hex colours; ignore
  }
  return wa;
}

export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  getWebApp()?.HapticFeedback.impactOccurred(style);
}

export function notify(type: 'success' | 'warning' | 'error') {
  getWebApp()?.HapticFeedback.notificationOccurred(type);
}

// Opens an external https link without closing the mini app. Telegram's
// `openLink` keeps the bot session alive; the `window.open` fallback covers
// browsers where the SDK isn't loaded (local dev).
export function openExternalLink(url: string) {
  const wa = getWebApp();
  if (wa?.openLink) {
    wa.openLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener');
}

// Opens a t.me/<bot>[/<app>] URL inside Telegram chrome. Use this for any
// destination that *is* itself a Telegram link — opening a t.me/ URL with
// the generic openLink first routes the user through Telegram's in-app
// browser, which then bounces back into Telegram on tap (extra step, ugly
// loading screen). openTelegramLink hands the URL straight to the client.
// Falls back to openExternalLink if the host doesn't expose the typed
// helper (older Telegram versions, local browser dev).
export function openTelegramLink(url: string) {
  const wa = getWebApp();
  if (wa?.openTelegramLink) {
    wa.openTelegramLink(url);
    return;
  }
  openExternalLink(url);
}

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

// Promise-shaped wrapper over Telegram's WebApp.openInvoice. Resolves with
// the final invoice status the user closed the modal on; rejects with
// 'unsupported' when the host client doesn't expose openInvoice (older
// Telegram, dev browser). The caller is expected to refresh `me` only on
// 'paid'.
export function openStarsInvoice(url: string): Promise<InvoiceStatus> {
  return new Promise((resolve, reject) => {
    const wa = getWebApp();
    if (!wa?.openInvoice) {
      reject(new Error('unsupported'));
      return;
    }
    try {
      wa.openInvoice(url, (status) => resolve(status));
    } catch (err) {
      reject(err);
    }
  });
}

// Best-effort write to Telegram CloudStorage. Resolves true on success,
// false on any error (storage unavailable, write rejected). Never throws —
// callers can fire-and-forget.
export function cloudSet(key: string, value: string): Promise<boolean> {
  return new Promise((resolve) => {
    const cs = getWebApp()?.CloudStorage;
    if (!cs) {
      resolve(false);
      return;
    }
    try {
      cs.setItem(key, value, (err, ok) => resolve(!err && Boolean(ok)));
    } catch {
      resolve(false);
    }
  });
}

// Best-effort read from Telegram CloudStorage. Resolves with the stored
// string on success, null when the key is missing OR CloudStorage isn't
// available (older clients, browser dev). Never throws.
export function cloudGet(key: string): Promise<string | null> {
  return new Promise((resolve) => {
    const cs = getWebApp()?.CloudStorage;
    if (!cs) {
      resolve(null);
      return;
    }
    try {
      cs.getItem(key, (err, value) => resolve(err ? null : value ?? null));
    } catch {
      resolve(null);
    }
  });
}
